/**
 * Loading / error / empty blocks shared by the portal pages (tracker item 45).
 * Mobile first: full-width blocks, 44px buttons, no horizontal scroll.
 */
import React from 'react';
import { PortalFailure } from '../../utils/portal';
import { secondaryButtonClass } from '../../components/formStyles';

export function LoadingBlock({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="flex justify-center py-10" role="status" aria-label={label}>
      <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-blue-500" />
    </div>
  );
}

/** A failed load: the message of the kind of failure, and Try again unless retrying cannot help. */
export function FailureBlock({ failure, onRetry }: { failure: PortalFailure; onRetry?: () => void }) {
  const canRetry = !!onRetry && failure.kind !== 'disabled' && failure.kind !== 'session';
  return (
    <div role="alert" className="rounded-lg bg-red-50 p-4 text-sm text-red-800 ring-1 ring-red-200" data-failure={failure.kind}>
      <p>{failure.message}</p>
      {failure.kind === 'rate-limit' && <p className="mt-1 text-xs text-red-700">Your account is fine; we slow requests down briefly to keep it safe.</p>}
      {canRetry && (
        <button type="button" className={secondaryButtonClass + ' mt-3'} onClick={onRetry}>Try again</button>
      )}
    </div>
  );
}

export function EmptyBlock({ children }: { children: React.ReactNode }) {
  return <p className="rounded-lg bg-white p-6 text-center text-sm text-gray-600 ring-1 ring-gray-200">{children}</p>;
}

/** An inline form or action problem (not a page load failure). */
export function InlineAlert({ children, kind = 'error' }: { children: React.ReactNode; kind?: 'error' | 'success' | 'info' }) {
  const styles = {
    error: 'bg-red-50 text-red-800 ring-red-200',
    success: 'bg-green-50 text-green-800 ring-green-200',
    info: 'bg-blue-50 text-blue-900 ring-blue-200',
  }[kind];
  return <div role={kind === 'error' ? 'alert' : 'status'} className={`rounded-lg p-3 text-sm ring-1 ${styles}`}>{children}</div>;
}
